import React, { useState, useContext, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import UserContext from './UserContext'; // Assuming UserContext provides authUser with token
import { API_BASE_URL } from './Vars';
import { useNavigate } from 'react-router-dom';

function AdminScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [queueLength, setQueueLength] = useState(null); // State for queue length
  const { authUser } = useContext(UserContext);

  // Function to fetch queue length
  const fetchQueueLength = async () => {
    if (!authUser || !authUser.accessToken) {
      console.error('Fetch Queue Length: Authentication token not found.');
      // Optionally set an error message for queue length display
      setQueueLength('Error: Auth token not found');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/admin/race-queue-length`, {
        method: 'GET',
        headers: {
          'x-access-token': authUser.accessToken,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setQueueLength(data.queueLength);
      } else {
        const data = await response.json();
        console.error('Failed to fetch queue length:', data.message);
        setQueueLength(`Error: ${data.message || 'Failed to fetch'}`);
      }
    } catch (error) {
      console.error('Error fetching queue length:', error);
      setQueueLength('Error: Network or server issue');
    }
  };

  useEffect(() => {
    fetchQueueLength(); // Fetch immediately on mount
    const intervalId = setInterval(fetchQueueLength, 1000); // Fetch every 1 second

    return () => {
      clearInterval(intervalId); // Cleanup interval on unmount
    };
  }, [authUser]); // Dependency array includes authUser to re-run if authUser changes

  const handleClearRaceQueue = async () => {
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    if (!authUser || !authUser.accessToken) {
      setErrorMessage('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/clear-race-queue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': authUser.accessToken,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message || 'Race queue cleared successfully!');
      } else {
        setErrorMessage(data.message || 'Failed to clear race queue.');
      }
    } catch (error) {
      console.error('Error clearing race queue:', error);
      setErrorMessage('An error occurred while clearing the race queue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="div">
          Admin Panel
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{ ml: 2 }}
        >
          Back to Main
        </Button>
      </Box>
      <Typography variant="h6" gutterBottom component="div" sx={{ mt: 2, mb: 2 }}>
        Current Race Queue Length: {queueLength !== null ? queueLength : 'Loading...'}
      </Typography>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleClearRaceQueue}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        Clear Race Queue
      </Button>
      {successMessage && (
        <Typography color="success.main" sx={{ mt: 2 }}>
          {successMessage}
        </Typography>
      )}
      {errorMessage && (
        <Typography color="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
}

export default AdminScreen;
